/* eslint-disable max-lines */
/* eslint-disable react/boolean-prop-naming */
/* eslint-disable react/jsx-handler-names */

import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import PropTypes from "prop-types";
import Typography from "../../../atoms/typography";
import TextField from "../../../atoms/textField";
import CustomImage from "../../../atoms/customImage";
import Regex from "../../../../utils/regex";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  handlePersonalizationDetailsOnRefresh,
  handleSetUserPersonalizations,
} from "../../../../utils/pdp/personalization";
import ButtonComponent from "../../../atoms/button";
import Drawer from "../../drawer";
import Toast from "../../../atoms/toast";
import { CAKE_TYPE, constants } from "../../../../constants/pageConstants/pdpConstants";
import { setUserPersonalization } from "../../../../store/slices/personalizationSlice";
import useAnalytics from "../../../../analytics/useAnalytics";
import { cleverTapCommonEvents, cleverTapCommonWords, validationFor } from "../../../../analytics/clevertapConstants";
import { COUNTRIES } from "../../../../constants/common/common";
import { setPersonalizationErrorFromAddToCart } from "../../../../store/slices/pdpSlice";
import { calDiscountPercentage, getProductDeliveryType, pageName, processDeliveryDate } from "../../../../utils/common";
import { unlockBody } from "../../../../utils/document";

/**
 * TextPersonalization component handles the rendering and logic for text input personalization.
 * It supports both mobile and desktop views, with responsive drawer input handling.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} props.isMobile - Determines if the view is for mobile.
 * @param {Object} props.option - Personalization option object containing configurations like label, maxSize, etc.
 * @param {number} props.textTypeCount - Number of text fields for the personalization.
 * @param {number} [props.indexNumber=0] - Index of the text field when multiple personalization options are available.
 * @returns {JSX.Element} The rendered component.
 */
const TextPersonalization = ({
  isMobile,
  option,
  textTypeCount,
  indexNumber = 0,
  multiText = false,
  isInvalid = false,
  onInputChange,
  productId,
  setTextsAddedSuccessfully,
  imageWithText = false,
  setMultiTextPersonalization,
  currentItem,
}) => {
  const { fieldName, allowedMediaType, contentType, required, label, maxSize } = option;

  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [textFieldValue, setTextFieldValue] = useState("");
  const [inputTextDrawer, setInputTextDrawer] = useState(false);
  const [textToastVisible, setTextToastVisible] = useState(false);
  const [deviceSpecificLabel, setDeviceSpecificLabel] = useState("");
  const pathname = usePathname();

  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");

  const userPersonalization = useSelector((state) => state.personalization.userPersonalization);
  const multiImageText = useSelector((state) => state.personalization.multiImageText);
  const clientVariantType = useSelector((state) => state.pdp?.clientVariantType);
  const {
    nextAvailableDifference,
    catalogId,
    pinCode,
    addRess,
    selectedIntLocation,
    deliveryDetailsData,
    deliveryDates,
  } = useSelector(
    (state) => ({
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      catalogId: state?.appConfig?.catalogId,
      pinCode: state?.locationLock?.selectedDomesticLocation?.pincode,
      addRess: state?.locationLock?.selectedDomesticLocation?.address,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
      deliveryDetailsData: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
    }),
    shallowEqual,
  );

  const clearPersonalizedData = useSelector((state) => state.personalization.clearPersonalizedData);
  const personalizationError = useSelector((state) => state.pdp.personalizationError);

  useEffect(() => {
    if (clearPersonalizedData) {
      if (multiText || imageWithText) {
        setMultiTextPersonalization([]);
        setTextsAddedSuccessfully(false);
      } else {
        setTextFieldValue("");
        if (isMobile) {
          setDeviceSpecificLabel(label);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPersonalizedData]);

  useEffect(() => {
    if (personalizationError) {
      const cleverTapData = {
        page_name: pageName(isHome, isPLP, pathname, currentItem?.urlIdentifier),
        pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
        validation_text: constants.THIS_IS_REQUIRED,
        validation_for: validationFor.NAME_TO_PRINT,
      };

      trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);
    }
  }, [personalizationError]);

  useEffect(() => {
    // Fetch data from local storage on component load
    const personalizationFromLocalStore = handlePersonalizationDetailsOnRefresh(productId);

    if (Array.isArray(personalizationFromLocalStore) && personalizationFromLocalStore.length > 0) {
      dispatch(setUserPersonalization(personalizationFromLocalStore));

      if (multiText) {
        setTextsAddedSuccessfully(true);
      }

      if (imageWithText || multiText) {
        const filteredPersonalization = personalizationFromLocalStore.filter(
          (entry) => entry.contentType === contentType,
        );

        setMultiTextPersonalization(filteredPersonalization);
      }

      const matchingEntry = personalizationFromLocalStore.find(
        (entry) => entry.fieldName === fieldName && entry.value?.[0],
      );

      if (matchingEntry) {
        setTextFieldValue(matchingEntry.value[0]);
        if (isMobile) {
          setDeviceSpecificLabel(matchingEntry.value[0]);
        }
      }
    } else if (!textFieldValue && multiText) {
      multiImageText?.forEach((entry) => {
        if (entry?.fieldName === fieldName && entry.value?.[0] !== textFieldValue) {
          setTextFieldValue(entry.value[0]);
          onInputChange(entry.value[0]);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, productId]);

  const personalizationValue = {
    fieldName,
    contentType,
    label,
    index: indexNumber,
    value: [textFieldValue],
  };

  useEffect(() => {
    if (!isMobile && !multiText) {
      handleSetUserPersonalizations(productId, personalizationValue, userPersonalization, dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, multiText, textFieldValue]);

  /**
   * Handles the change event for the text field, ensuring the value matches the allowed regex and length.
   *
   * @param {Object} event - The event object from the text field.
   */
  const onChangeText = (event) => {
    const { value } = event.target;
    if (Regex.alphaNumericWithSpecialChar.test(value)) {
      const validatedValue = value.length > Number(maxSize) ? value.substr(0, Number(maxSize)) : value;
      setTextFieldValue(validatedValue);
      if (multiText) {
        onInputChange(validatedValue);
      }
    }
  };

  /**
   * Returns the appropriate icon URL based on the field name.
   *
   * @param {string} fieldName - The name of the field used to determine the icon.
   * @returns {string} The icon URL.
   */
  const renderIconsBasedOnFieldName = (fieldName) => {
    return fieldName === "msgOnCake" ? `/icons/birthday-gray.svg` : `/icons/message-text.svg`;
  };

  /**
   * Returns the label text based on the field name.
   *
   * @param {string} fieldName - The name of the field used to determine the label.
   * @returns {string} The label text.
   */
  const renderTextBasedOnFieldName = (fieldName) => {
    return fieldName === "msgOnCake" ? constants.MESSAGE_ON_CAKE : constants.ADD_PERSONALISED_TEXT;
  };

  /**
   * Determines if the text field is required based on the personalization option.
   *
   * @param {string} requiredFlag - The required attribute from the personalization option.
   * @returns {boolean} True if the field is required, false otherwise.
   */
  const isRequired = (requiredFlag) => requiredFlag === "Y";

  const discountPercentage = calDiscountPercentage(
    currentItem?.productPrice?.listPrice,
    currentItem?.productPrice?.price,
  );
  const productDeliveryType = getProductDeliveryType(currentItem?.primaryCategoryId);
  const commonCleverTapData = {
    page_type: cleverTapCommonWords.PDP,
    page_name: currentItem?.urlIdentifier,
    product_name: currentItem?.productName,
    pid: currentItem?.productId,
    product_category: currentItem?.primaryCategoryId,
    is_variant_available: currentItem?.isVariant === "Y",
    is_egg: clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false,
    delivery_type: productDeliveryType,
    product_price: currentItem?.productPrice?.price,
    strike_price: currentItem?.productPrice?.listPrice,
    discount_percentage: discountPercentage,
    is_international: catalogId !== COUNTRIES.INDIA,
    city: catalogId !== COUNTRIES.INDIA ? selectedIntLocation : "",
    area: catalogId !== COUNTRIES.INDIA ? "" : addRess,
    pincode: catalogId !== COUNTRIES.INDIA ? "" : pinCode,
    system_pincode: catalogId !== COUNTRIES.INDIA ? "" : pinCode,
    country: catalogId,
    date_of_delivery: processDeliveryDate(deliveryDetailsData?.selectedDate),
    next_available_date_difference: catalogId !== COUNTRIES.INDIA ? "" : nextAvailableDifference?.nextAvailableDateDiff,
    delivery_shipping: deliveryDetailsData?.currentShippingMethod?.shippingMethodName || "",
    delivery_shipping_fee: deliveryDetailsData?.timeSlots?.timeSlots?.shippingPrice ?? 0,
    delivery_slot: deliveryDetailsData?.timeSlots?.formatTime || "",
    product_rating: currentItem?.productReviewsAndRatings?.ratingValue,
    product_review_count: currentItem?.productReviewsAndRatings?.totalReview,
    earliest_delivery_date: deliveryDates?.deliveryDates?.[0] ?? "",
  };

  /**
   * Handles the save button click for the text input drawer.
   */
  const onSavePersonalizationHandler = () => {
    const checkSpecificTextFieldValue = fieldName === "msgOnCake" ? true : textFieldValue;
    if (checkSpecificTextFieldValue && isMobile) {
      textFieldValue && setTextToastVisible(true);
      setInputTextDrawer(false);
      setDeviceSpecificLabel(textFieldValue);
      handleSetUserPersonalizations(productId, personalizationValue, userPersonalization, dispatch);
      if (fieldName === "msgOnCake") {
        trackEvent(
          cleverTapCommonEvents.messageOnCakeEntered,
          {
            ...commonCleverTapData,
            cake_message: textFieldValue,
          },
          ["clevertap"],
        );
      } else {
        trackEvent(
          cleverTapCommonEvents.nameToPrintEntered,
          {
            ...commonCleverTapData,
            name_to_print: textFieldValue,
          },
          ["clevertap"],
        );
      }
      unlockBody();
    }
  };

  return (
    <div className={`flex flex-col gap-12 ${multiText ? " pb-[16px]" : ""}`}>
      {textTypeCount === 1 && (
        <Typography variant="p" Tag="p" classes="font-600 text-16 text-fnp-500" id="PersonalizedText-label">
          {renderTextBasedOnFieldName(fieldName)}
        </Typography>
      )}
      {isMobile ? (
        <TextField
          variant="primary"
          name={fieldName}
          id={fieldName}
          type="text"
          inputValue={deviceSpecificLabel || label}
          classes={"text-grey-500 font-inter text-14 font-normal capitalize text-left"}
          onClick={() => setInputTextDrawer(true)}
          readOnly={true}
          isRequired={false}
          startAdornment={
            <CustomImage
              src={renderIconsBasedOnFieldName(fieldName)}
              width={24}
              height={24}
              alt="start icon"
              sizes="10vw"
            />
          }
          endAdornment={
            <CustomImage src="/icons/arrow-right-dark.svg" width={12} height={12} alt="arrow icon" sizes="10vw" />
          }
          isError={(textFieldValue === "" && personalizationError) || isInvalid}
          message={personalizationError ? constants.THIS_IS_REQUIRED : ""}
          messageClass="w-full pe-16"
        />
      ) : (
        <TextField
          variant="primary"
          name={fieldName}
          id={fieldName}
          type="text"
          inputValue={textFieldValue}
          onChange={onChangeText}
          autoFocus={multiText && indexNumber === 0 ? true : false}
          accept={allowedMediaType}
          onFocus={() => dispatch(setPersonalizationErrorFromAddToCart(false))}
          placeholder={label}
          isRequired={isRequired(required)}
          isError={(textFieldValue === "" && personalizationError) || isInvalid}
          startAdornment={
            multiText ? null : (
              <CustomImage
                src={renderIconsBasedOnFieldName(fieldName)}
                width={24}
                height={24}
                alt="start icon"
                sizes="10vw"
              />
            )
          }
          endAdornment={
            <>
              <div className="text-gray-400">{`${textFieldValue?.length}/${Number(maxSize)}`}</div>
            </>
          }
          label={multiText ? label : ""}
          message={personalizationError ? constants.THIS_IS_REQUIRED : ""}
          messageClass="w-full md:w-[254px] pe-16"
        />
      )}

      <Drawer position="bottom" isOpen={inputTextDrawer} onDrawerClose={() => setInputTextDrawer(false)}>
        <div className="mx-16 my-8 flex flex-col items-start gap-24 p-4">
          <TextField
            variant="primary"
            inputValue={textFieldValue}
            name={fieldName}
            id={fieldName}
            autoFocus={true}
            accept={allowedMediaType}
            onChange={onChangeText}
            placeholder={label}
            type="text"
            endAdornment={
              <>
                <div className="text-gray-400">{`${textFieldValue?.length}/${Number(maxSize)}`}</div>
              </>
            }
            isRequired={false}
          />
          <ButtonComponent
            size="small"
            classes="text-base flex justify-center"
            buttonVariant="filled"
            label="Save Message"
            onClick={onSavePersonalizationHandler}
            isRemoveExtraPadding={true}
          />
        </div>
      </Drawer>

      <div className="fixed bottom-[120px] left-1/2 z-20 flex w-full max-w-md -translate-x-1/2 transform items-center justify-center">
        <Toast
          variant="success"
          isToast={textToastVisible}
          header="Changes saved successfully"
          duration={3000}
          onToastChange={() => setTextToastVisible(false)}
        />
      </div>
    </div>
  );
};

TextPersonalization.propTypes = {
  isMobile: PropTypes.bool,
  option: PropTypes.shape({
    fieldName: PropTypes.string.isRequired,
    allowedMediaType: PropTypes.string,
    contentType: PropTypes.string,
    required: PropTypes.string,
    minInputRequired: PropTypes.string,
    label: PropTypes.string.isRequired,
    maxSize: PropTypes.string.isRequired,
  }).isRequired,
  textTypeCount: PropTypes.number,
  indexNumber: PropTypes.number,
  multiText: PropTypes.bool,
  isInvalid: PropTypes.bool,
  imageWithText: PropTypes.bool,
  onInputChange: PropTypes.func,
  setMultiTextPersonalization: PropTypes.func,
  setTextsAddedSuccessfully: PropTypes.func,
  productId: PropTypes.string.isRequired,
  currentItem: PropTypes.object,
};

export default TextPersonalization;
